import React, { useEffect, useState } from 'react';
import { Heart, MessageCircle, Lock, UserPlus, UserCheck, Loader2, X, Send, Image as ImageIcon, ChevronRight, Sparkles } from 'lucide-react';
import SheetOrPage from './SheetOrPage';
import { toSlug } from '../../utils/slug';
import { useStorageUrl } from './MemberAvatar';
import { useToast } from '../../contexts/NotificationContext';
import TopUpCard from '../wallet/TopUpCard';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import {
  getAgentGallery, getPrivateGallery, generatePrivatePost, likeGalleryPost, followAgent, unlockAgentGallery,
  getGalleryComments, commentGalleryPost,
  ASPECT_CLASS, type GalleryPost, type GalleryComment, type AgentGalleryView, type GalleryScope,
} from '../../services/galleryService';
import { TYPE } from '../../constants/typeScale';

// Note: liking / commenting / following still grows the agent's rapport (Link) server-side for
// personalization + proactive check-ins — it's just no longer surfaced to the user as a toast.

/** One post tile — resolves its Storage image; a locked shot shows a lock until the gallery is unlocked. */
const PostTile: React.FC<{ post: GalleryPost; liked: boolean; onLike: () => void; onOpen: () => void }> = ({ post, liked, onLike, onOpen }) => {
  const url = useStorageUrl(post.image_path);
  // Degrade to the placeholder if the resolved image fails to load (object not in this env / 404) rather
  // than showing a broken-image icon.
  const [broken, setBroken] = useState(false);
  return (
    <div className="space-y-1.5">
      <button onClick={onOpen} className={`relative w-full overflow-hidden rounded-xl bg-secondary-100 dark:bg-secondary-800 ${ASPECT_CLASS[post.aspect] || 'aspect-square'}`}>
        {post.image_path && url && !broken ? (
          <img src={url} alt={post.caption} onError={() => setBroken(true)} className="w-full h-full object-cover" />
        ) : post.locked ? (
          // Locked = an inviting "there's something here" tease (drives the subscribe), not a blank lock box. The
          // image itself is withheld server-side (never sent to a non-subscriber), so this is a styled placeholder.
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-secondary-200 to-secondary-300 text-secondary-500 dark:from-secondary-700 dark:to-secondary-800 dark:text-secondary-300">
            <Lock className="w-5 h-5" />
            <span className={`${TYPE.micro} font-medium`}>Subscribe to reveal</span>
          </span>
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-secondary-300 dark:text-secondary-600"><Loader2 className="w-5 h-5 animate-spin" /></span>
        )}
      </button>
      <div className="flex items-center gap-3 px-0.5 text-secondary-500 dark:text-secondary-300">
        <button onClick={onLike} className={`inline-flex items-center gap-1 ${TYPE.control} hover:text-secondary-900 dark:hover:text-white`}>
          <Heart className={`w-4 h-4 ${liked ? 'fill-current text-secondary-900 dark:text-white' : ''}`} /> {post.like_count || 0}
        </button>
        <button onClick={onOpen} className={`inline-flex items-center gap-1 ${TYPE.control} hover:text-secondary-900 dark:hover:text-white`}>
          <MessageCircle className="w-4 h-4" /> {post.comment_count || 0}
        </button>
      </div>
    </div>
  );
};

/** A tiny thumbnail for the 3-photo strip shown under an agent's name (opens the full gallery). */
const StripThumb: React.FC<{ post: GalleryPost }> = ({ post }) => {
  const url = useStorageUrl(post.image_path);
  const [broken, setBroken] = useState(false);
  return (
    <span className="w-9 h-9 rounded-lg overflow-hidden bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center shrink-0">
      {post.image_path && url && !broken ? <img src={url} alt="" onError={() => setBroken(true)} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-secondary-300 dark:text-secondary-600" />}
    </span>
  );
};

/** The first-3-photos strip under an agent's name — a clear, labelled entry to the agent's social feed. */
export const GalleryStrip: React.FC<{ posts: GalleryPost[]; onOpen: () => void }> = ({ posts, onOpen }) => {
  const three = posts.slice(0, 3);
  if (!three.length) return null;
  return (
    <button onClick={onOpen} className="group inline-flex items-center gap-1.5 rounded-xl border border-secondary-200 dark:border-secondary-600 p-1 pr-2 hover:border-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-800/50" aria-label="Open social gallery">
      {three.map((p) => <StripThumb key={p.id} post={p} />)}
      <span className={`ml-0.5 inline-flex items-center gap-0.5 ${TYPE.control} font-medium text-secondary-600 dark:text-secondary-300`}>
        Social <span className="text-secondary-400">· {posts.length}</span>
        <ChevronRight className="w-3.5 h-3.5 text-secondary-400 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
};

interface Props { open: boolean; asPage?: boolean; agentId: string; agentName: string; initialTab?: 'feed' | 'yours'; onClose: () => void; onWalletChanged?: () => void }

const AgentGallery: React.FC<Props> = ({ open, asPage, agentId, agentName, initialTab = 'feed', onClose, onWalletChanged }) => {
  const { showError } = useToast();
  const [tab, setTab] = useState<'feed' | 'yours'>(initialTab);
  const [view, setView] = useState<AgentGalleryView | null>(null);
  const [priv, setPriv] = useState<{ posts: GalleryPost[]; liked: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [needTopUp, setNeedTopUp] = useState(false);
  const [active, setActive] = useState<{ post: GalleryPost; scope: GalleryScope } | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      getAgentGallery(agentId).then(setView).catch(() => setView(null)),
      getPrivateGallery(agentId).then((r) => setPriv({ posts: r.posts, liked: r.liked })).catch(() => setPriv({ posts: [], liked: [] })),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => {
    if (open && agentId) { setActive(null); setTab(initialTab); setNeedTopUp(false); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  if (!open) return null;
  const pubLiked = new Set(view?.liked || []);
  const privLiked = new Set(priv?.liked || []);
  const hasLocked = (view?.posts || []).some((p) => p.locked) && !view?.unlocked;
  const posts = tab === 'feed' ? (view?.posts || []) : (priv?.posts || []);
  const likedSet = tab === 'feed' ? pubLiked : privLiked;
  const scope: GalleryScope = tab === 'feed' ? 'public' : 'private';

  const toggleFollow = async () => {
    if (!view) return;
    setBusy(true);
    try { const r = await followAgent(agentId, !view.following); setView({ ...view, following: r.following }); }
    catch { /* ignore */ } finally { setBusy(false); }
  };

  const like = async (post: GalleryPost, sc: GalleryScope) => {
    const wasLiked = (sc === 'private' ? privLiked : pubLiked).has(post.id);
    const bump = (p: GalleryPost) => p.id === post.id ? { ...p, like_count: Math.max(0, p.like_count + (wasLiked ? -1 : 1)) } : p;
    if (sc === 'private' && priv) setPriv({ ...priv, liked: wasLiked ? priv.liked.filter((x) => x !== post.id) : [...priv.liked, post.id], posts: priv.posts.map(bump) });
    else if (sc === 'public' && view) setView({ ...view, liked: wasLiked ? view.liked.filter((x) => x !== post.id) : [...view.liked, post.id], posts: view.posts.map(bump) });
    try { await likeGalleryPost(agentId, post.id, sc); }
    catch { load(); /* reconcile */ }
  };

  // Reflect a newly-added comment (user + any agent reply) in the grid counter without a full reload.
  const bumpComments = (postId: string, delta: number) => {
    const bump = (p: GalleryPost) => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + delta } : p;
    setView((v) => v ? { ...v, posts: v.posts.map(bump) } : v);
    setPriv((pv) => pv ? { ...pv, posts: pv.posts.map(bump) } : pv);
    setActive((a) => a ? { ...a, post: bump(a.post) } : a);
  };

  const unlock = async () => {
    setBusy(true);
    try { await unlockAgentGallery(agentId); onWalletChanged?.(); load(); }
    catch (e: any) { showError(/not_enough/i.test(e?.message || '') ? 'Out of credits — top up to unlock' : 'Could not unlock the gallery'); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setGenerating(true); setNeedTopUp(false);
    try {
      const { post } = await generatePrivatePost(agentId);
      setPriv((pv) => ({ posts: [post, ...(pv?.posts || [])], liked: pv?.liked || [] }));
      onWalletChanged?.();
    } catch (e: any) {
      if (/not_enough/i.test(e?.message || '')) setNeedTopUp(true);
      else showError('Could not create a post right now');
    } finally { setGenerating(false); }
  };

  return (
    <SheetOrPage open={open} asPage={asPage} title={`${agentName} · Gallery`} onClose={onClose} maxWidth="max-w-lg">
      <div className="p-4 space-y-4">
        {/* Feed (shared) vs Yours (private, generated for you) */}
        <div className={`flex items-center gap-1 rounded-full bg-secondary-100 dark:bg-secondary-800 p-1 ${TYPE.control} font-medium`}>
          {(['feed', 'yours'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 rounded-full px-3 py-1.5 transition-colors ${tab === t ? 'bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white shadow-sm' : 'text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-200'}`}>
              {t === 'feed' ? 'Feed' : 'Yours'}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <p className={`${TYPE.control} text-secondary-400`}>{loading ? '' : `${posts.length} ${tab === 'yours' ? 'private ' : ''}post${posts.length === 1 ? '' : 's'}`}</p>
          {tab === 'feed' && (
            <button onClick={toggleFollow} disabled={busy || !view}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ${TYPE.control} font-semibold transition-colors ${view?.following
                ? 'border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-200'
                : 'bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100'}`}>
              {view?.following ? <><UserCheck className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
            </button>
          )}
        </div>

        {loading && <div className={`flex items-center gap-2 py-8 justify-center ${TYPE.body} text-secondary-400`}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!loading && posts.length === 0 && (
          <p className={`py-8 text-center ${TYPE.body} text-secondary-400`}>{tab === 'yours' ? 'No private posts yet — create your first below.' : 'No photos yet.'}</p>
        )}
        {!loading && posts.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {posts.map((p) => (
              <PostTile key={p.id} post={p} liked={likedSet.has(p.id)} onLike={() => like(p, scope)}
                onOpen={() => (tab === 'feed' && p.locked && !view?.unlocked) ? undefined : setActive({ post: p, scope })} />
            ))}
          </div>
        )}

        {/* Feed: unlock the shared paywalled shots */}
        {tab === 'feed' && hasLocked && (
          <button onClick={unlock} disabled={busy}
            className={`w-full inline-flex items-center justify-center gap-2 rounded-xl bg-secondary-900 dark:bg-white px-4 py-2.5 ${TYPE.sectionTitle} text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-50`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Unlock all photos
          </button>
        )}

        {/* Yours: generate a fresh, random, private post */}
        {tab === 'yours' && (
          <div className="space-y-1.5">
            <button onClick={generate} disabled={generating}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-xl bg-secondary-900 dark:bg-white px-4 py-2.5 ${TYPE.sectionTitle} text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-50`}>
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating a fresh post…</> : <><Sparkles className="w-4 h-4" /> Generate a new post</>}
            </button>
            <p className={`text-center ${TYPE.meta} text-secondary-400`}>A surprise new look every time — private to you.</p>
            {needTopUp && <div className="pt-1"><TopUpCard /></div>}
          </div>
        )}
      </div>

      {active && <PostLightbox agentId={agentId} post={active.post} scope={active.scope} onClose={() => setActive(null)} onCommented={bumpComments} />}
    </SheetOrPage>
  );
};

/** Full-screen post view with the comment thread (the agent replies to your comments, in character). */
const PostLightbox: React.FC<{ agentId: string; post: GalleryPost; scope: GalleryScope; onClose: () => void; onCommented?: (postId: string, delta: number) => void }> = ({ agentId, post, scope, onClose, onCommented }) => {
  const url = useStorageUrl(post.image_path);
  const [comments, setComments] = useState<GalleryComment[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => { getGalleryComments(agentId, post.id, scope).then(setComments).catch(() => setComments([])); }, [agentId, post.id, scope]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await commentGalleryPost(agentId, post.id, t, scope);
      setComments((c) => [...c, r.comment, ...(r.agentReply ? [r.agentReply] : [])]);
      setText('');
      // Reflect the new comment(s) in the grid counter: the user's comment + any in-character agent reply.
      onCommented?.(post.id, 1 + (r.agentReply ? 1 : 0));
    } catch { /* ignore */ } finally { setSending(false); }
  };

  // Escape closes the lightbox (a11y — keyboard users + a fast dismiss without reaching for the X).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const trapRef = useFocusTrap<HTMLDivElement>();

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4" onClick={onClose}>
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label={post.caption ? `Post: ${post.caption}` : 'Gallery post'}
        className="relative w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-2xl bg-white dark:bg-secondary-900" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-2 right-2 z-10 rounded-full bg-black/40 p-1.5 text-white/90 hover:text-white"><X className="w-4 h-4" /></button>
        {url && <img src={url} alt={post.caption} className="w-full object-cover rounded-t-2xl" />}
        <div className="p-4 space-y-3">
          {post.caption && <p className={`${TYPE.body} text-secondary-800 dark:text-secondary-100`}>{post.caption}</p>}
          <div className="space-y-2">
            {comments.length === 0 && <p className={`${TYPE.control} text-secondary-400`}>No comments yet — say something nice.</p>}
            {comments.map((c) => {
              const isAgent = typeof c.by === 'string' && c.by.startsWith('agent:');
              const commenterId = isAgent ? c.by.slice('agent:'.length) : null;
              // A real-agent commenter's name links to their public profile (Instagram-style ecosystem);
              // opens in a new tab so the user keeps their place in this chat/gallery.
              // The public profile route is /agent/{name-slug} (not the id), so link by the commenter's name.
              const nameEl = commenterId
                ? <a href={`/agent/${toSlug(c.name)}`} target="_blank" rel="noopener noreferrer"
                    className="font-semibold text-secondary-900 dark:text-white hover:underline">{c.name}</a>
                : <span className="font-semibold text-secondary-900 dark:text-white">{c.name}</span>;
              return (
                <div key={c.id} className={`${TYPE.control} ${isAgent ? 'pl-3 border-l-2 border-secondary-300 dark:border-secondary-600' : ''}`}>
                  {nameEl} <span className="text-secondary-600 dark:text-secondary-300">{c.text}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder="Add a comment…"
              className={`flex-1 rounded-full border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-1.5 ${TYPE.body} text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500`} />
            <button onClick={send} disabled={!text.trim() || sending} className="rounded-full bg-secondary-900 dark:bg-white p-2 text-white dark:text-secondary-900 disabled:opacity-40">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentGallery;
